/**
 * ExportModal - Generic Export Modal Component
 *
 * This component provides a scalable export modal that can handle different entities (users, patients, projects).
 * The modal allows users to export data in various formats (xlsx, xls, csv, txt, pdf) with customizable column selection.
 *
 * ## Architecture
 *
 * The component uses a type-safe entity-to-columns mapping system that ensures:
 * - Type safety: Custom columns are correctly typed based on the entity
 * - Scalability: New entities can be easily added without breaking existing functionality
 * - Flexibility: Each entity can have its own set of exportable columns
 *
 * ## Adding a New Entity
 *
 * To add support for a new entity (e.g., 'treatments'), follow these steps:
 *
 * 1. Define the column enum in the entity's types file:
 *    ```typescript
 *    // src/features/treatments/types.ts
 *    export const TreatmentsColumn = z.enum([
 *      'select',
 *      'name',
 *      'status',
 *      'date',
 *      'moreActions'
 *    ])
 *    export type TreatmentsColumn = z.infer<typeof TreatmentsColumn>
 *    ```
 *
 * 2. Import the column type in this file:
 *    ```typescript
 *    import {TreatmentsColumn} from '@/features/treatments/types'
 *    ```
 *
 * 3. Update the Entity type:
 *    ```typescript
 *    type Entity = 'users' | 'patients' | 'projects' | 'treatments'
 *    ```
 *
 * 4. Update EntityColumnsSchemaMap:
 *    ```typescript
 *    type EntityColumnsSchemaMap = {
 *      users: typeof UsersColumn
 *      patients: typeof PatientsColumn
 *      projects: typeof ProjectsColumn
 *      treatments: typeof TreatmentsColumn
 *    }
 *    ```
 *
 * 5. Add a case in getExcludedColumnsSchema:
 *    ```typescript
 *    case 'treatments':
 *      return TreatmentsColumn.exclude(['select', 'moreActions'])
 *    ```
 *
 * 6. (Optional) Create a custom columns selector component for the entity if needed,
 *    similar to ExportModalCustomColumns (currently used only for users)
 *
 * ## Usage Example
 *
 * ```typescript
 * <ExportModal
 *   entity="users"
 *   trigger={<Button>Export</Button>}
 *   promise={({extension, mode, customColumns}) => exportUsers({extension, mode, customColumns})}
 *   fileName="users-export"
 *   allowedExtensions={['xlsx', 'csv', 'pdf']}
 *   selectedCount={150}
 *   hasViews={true}
 * />
 * ```
 */

import {Button} from '@/components/ui/button/Button'
import {
    Dialog,
    DialogBody,
    DialogDisclosure,
    DialogDismiss,
    DialogFooter,
    DialogHeader,
    DialogProvider,
    DialogHeading,
    DialogDescription
} from '@/components/ui/dialog-atoms/DialogAtoms'
import {useAsync} from '@/hooks/useAsync'
import {TKey, Trans, useTranslation} from '@/translations/i18n'
import {buildFile, downloadFile} from '@/utilities/helpers'
import {zodResolver} from '@hookform/resolvers/zod'
import {AxiosError, AxiosResponse} from 'axios'
import {useState, useEffect, useRef} from 'react'
import {useDialogContext} from '@ariakit/react'
import {FormProvider, useForm, useWatch} from 'react-hook-form'
import {z} from 'zod'
import {StyledDescription, StyledExtensionRadioField, StyledSectionTitle} from './style'
import {Spinner} from '@/components/ui/spinner/Spinner'
import {AlertCircleIcon, CheckIcon} from '@/components/ui/icon/Icon'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {InputRadio} from '@/components/ui/input-radio/InputRadio'
import {RadioField} from '../radio-field/RadioField'
import {useTheme} from 'styled-components'
import {useSearchParams} from 'react-router-dom'
import {useGetView} from '@/features/views/queries/useGetView'
import {ExportModalProvider, useExportModalContext} from './ExportModalContext'
import {ExportModalCustomColumns} from './ExportModalCustomColumns'
import {flyToExportsToggle, prefersReducedMotion} from './flyToExports'
import {UsersColumn} from '@/features/users/types'
import {PatientsColumn} from '@/features/patients/types'
import {ProjectsColumn} from '@/features/projects/types'
import {queryClient} from '@/queryClient'
import {EXPORTS_LIST_KEY_PREFIX} from '@/features/exports/query/exportsKeys'
import {ExportJob} from '@/features/exports/types'
import {useExportsIndicatorStore} from '@/features/exports/hooks/useExportsIndicator'
import {useExportAnnouncementStore} from '@/features/exports/hooks/useExportAnnouncement'

const Extension = z.enum(['xlsx', 'xls', 'csv', 'txt', 'pdf'])
type Extension = z.infer<typeof Extension>

/**
 * Async-export setting. `true` enqueues every format asynchronously; a predicate enables async
 * per-extension (e.g. accesses export xlsx/csv async, but keep `txt`/`pdf` as synchronous document
 * downloads on their dedicated endpoints).
 */
type AsyncExportSetting = boolean | ((extension: Extension) => boolean)

export type Entity = 'users' | 'patients' | 'projects'

type EntityColumnsSchemaMap = {
    users: typeof UsersColumn
    patients: typeof PatientsColumn
    projects: typeof ProjectsColumn
}

type EntityColumnsMap = {
    [K in Entity]: z.infer<EntityColumnsSchemaMap[K]>
}

type GetEntityColumns<E extends Entity> = EntityColumnsMap[E]

export type GetExcludedEntityColumns<E extends Entity> = Exclude<GetEntityColumns<E>, 'select' | 'moreActions'>

const getExcludedColumnsSchema = <E extends Entity>(entity: E) => {
    switch (entity) {
        case 'users':
            return UsersColumn.exclude(['select', 'moreActions'])
        case 'patients':
            return PatientsColumn.exclude(['select', 'moreActions'])
        case 'projects':
            return ProjectsColumn.exclude(['select', 'moreActions'])
        default:
            throw new Error(`Unknown entity: ${entity}`)
    }
}

const extensionToLabel = {
    xlsx: 'XLSX',
    xls: 'XLS',
    csv: 'CSV',
    txt: 'TXT',
    pdf: 'PDF'
} as const satisfies Record<Extension, string>

const extensionToDescription = {
    xlsx: 'commons:extension_xlsx_description',
    xls: 'commons:extension_xls_description',
    csv: 'commons:extension_csv_description',
    txt: 'commons:extension_txt_description',
    pdf: 'commons:extension_pdf_description'
} as const satisfies Record<Extension, TKey>

interface ExportModalNextProps<E extends Entity, T extends Extension> {
    entity?: E
    trigger: React.ReactElement
    promise: ({
        extension,
        mode,
        customColumns
    }: {
        extension: T
        mode: ValidationSchema<E>['mode']
        customColumns?: GetExcludedEntityColumns<E>[]
    }) => Promise<AxiosResponse>
    fileName: string
    allowedExtensions: T[]
    selectedCount: number
    hasViews?: boolean
    errorsMap?: Record<string, TKey>
    /**
     * Enqueue the export asynchronously instead of downloading a file: no blob is parsed, the exports
     * history is refreshed and a "queued" state is shown. Pass `true` for all formats, or a predicate
     * to enable it per-extension (mixed modals that still download some formats synchronously).
     */
    asyncExport?: AsyncExportSetting
    /** Called after a successful async enqueue, in addition to the built-in exports-history refresh. */
    onAsyncSuccess?: () => void
}
export const ExportModal = <E extends Entity, T extends Extension>({
    entity,
    trigger,
    promise,
    fileName,
    allowedExtensions,
    selectedCount,
    hasViews = true,
    errorsMap,
    asyncExport = false,
    onAsyncSuccess
}: ExportModalNextProps<E, T>) => {
    return (
        <ExportModalProvider>
            <DialogProvider>
                <DialogDisclosure render={trigger} />
                <ExportDialogContent
                    entity={entity}
                    promise={promise}
                    fileName={fileName}
                    allowedExtensions={allowedExtensions}
                    selectedCount={selectedCount}
                    hasViews={hasViews}
                    errorsMap={errorsMap}
                    asyncExport={asyncExport}
                    onAsyncSuccess={onAsyncSuccess}
                />
            </DialogProvider>
        </ExportModalProvider>
    )
}

const ExportDialogContent = <E extends Entity, T extends Extension>({
    entity,
    promise,
    fileName,
    allowedExtensions,
    selectedCount,
    hasViews,
    errorsMap,
    asyncExport,
    onAsyncSuccess
}: {
    entity?: E
    promise: ({
        extension,
        mode,
        customColumns
    }: {
        extension: T
        mode: ValidationSchema<E>['mode']
        customColumns?: GetExcludedEntityColumns<E>[]
    }) => Promise<AxiosResponse>
    fileName: string
    allowedExtensions: T[]
    selectedCount: number
    hasViews?: boolean
    errorsMap?: Record<string, TKey>
    asyncExport?: AsyncExportSetting
    onAsyncSuccess?: () => void
}) => {
    const {t} = useTranslation()
    const {isExporting} = useExportModalContext()

    return (
        <Dialog width="400px" hideOnInteractOutside={!isExporting}>
            <DialogHeader>
                <DialogHeading>{t('commons:export')}</DialogHeading>
                <DialogDismiss disabled={isExporting} />
            </DialogHeader>
            <Form
                entity={entity}
                promise={promise}
                fileName={fileName}
                allowedExtensions={allowedExtensions}
                selectedCount={selectedCount}
                hasViews={hasViews}
                errorsMap={errorsMap}
                asyncExport={asyncExport}
                onAsyncSuccess={onAsyncSuccess}
            />
        </Dialog>
    )
}

const createValidationSchema = <E extends Entity>(entity: E | undefined) => {
    if (!entity) {
        return z.object({
            extension: Extension,
            mode: z.enum(['view', 'all'])
        })
    }

    const excludedColumnsSchema = getExcludedColumnsSchema(entity)
    return z.object({
        extension: Extension,
        mode: z.enum(['view', 'all', 'custom']),
        customColumns: z.array(excludedColumnsSchema)
    })
}

export type ValidationSchema<E extends Entity> = {
    extension: Extension
    mode: 'view' | 'all' | 'custom'
    customColumns?: GetExcludedEntityColumns<E>[]
}

const Form = <E extends Entity, T extends Extension>({
    entity,
    promise,
    fileName,
    allowedExtensions,
    selectedCount,
    hasViews,
    errorsMap,
    asyncExport,
    onAsyncSuccess
}: {
    entity?: E
    promise: ({
        extension,
        mode,
        customColumns
    }: {
        extension: T
        mode: ValidationSchema<E>['mode']
        customColumns?: GetExcludedEntityColumns<E>[]
    }) => Promise<AxiosResponse>
    fileName: string
    allowedExtensions: T[]
    selectedCount: number
    hasViews?: boolean
    errorsMap?: Record<string, TKey>
    asyncExport?: AsyncExportSetting
    onAsyncSuccess?: () => void
}) => {
    const {t} = useTranslation()
    const theme = useTheme()
    const dialog = useDialogContext()
    const exportAsync = useAsync()
    const [fileData, setFileData] = useState<Blob | null>(null)
    const [wasAsync, setWasAsync] = useState(false)
    const [error, setError] = useState<string | undefined>(undefined)
    const hasFlownRef = useRef(false)
    const enqueuedJobRef = useRef<ExportJob | null>(null)
    const {setIsExporting} = useExportModalContext()
    const [searchParams] = useSearchParams()
    const currentViewId = searchParams.get('view')
    const viewQuery = useGetView(currentViewId, {
        enabled: !!currentViewId
    })

    const validationSchema = createValidationSchema(entity)

    const form = useForm<ValidationSchema<E>>({
        resolver: zodResolver(validationSchema),
        defaultValues: {
            extension: allowedExtensions[0],
            mode: hasViews ? 'view' : 'all',
            customColumns: entity ? [] : undefined
        }
    })

    const {handleSubmit, register, control} = form

    const onSubmit = handleSubmit(async data => {
        const isAsync = typeof asyncExport === 'function' ? asyncExport(data.extension) : !!asyncExport
        setWasAsync(isAsync)
        try {
            const response = await exportAsync.run(
                promise({
                    extension: data.extension as T,
                    mode: data.mode,
                    customColumns: data.customColumns
                })
            )
            if (isAsync) {
                // Async export: the job is enqueued; nothing to download. Refresh the exports history
                // so the new pending job surfaces in the drawer, then run the optional extra callback.
                void queryClient.invalidateQueries({queryKey: EXPORTS_LIST_KEY_PREFIX})
                // Light up the toggle dot immediately (orange) without waiting for the first socket
                // event; the response body is the freshly-created pending ExportJob.
                const enqueued = ExportJob.safeParse(response.data)
                if (enqueued.success) {
                    useExportsIndicatorStore.getState().track(enqueued.data.id, enqueued.data.status)
                    // Stash the pending job so the fly animation can announce it on the toggle once it lands.
                    enqueuedJobRef.current = enqueued.data
                }
                onAsyncSuccess?.()
            } else {
                const parsedResponse = z.instanceof(Blob).parse(response.data)
                setFileData(parsedResponse)
                const file = buildFile({extension, fileName, data: parsedResponse})
                downloadFile(file)
            }
        } catch (error) {
            if (
                errorsMap &&
                error instanceof AxiosError &&
                typeof error.response?.data.code == 'string' &&
                error.response.data.code in errorsMap
            ) {
                setError(t(errorsMap[error.response.data.code]))
            } else {
                setError(t('errors:default'))
            }
        }
    })

    const extension = useWatch({control: control, name: 'extension'})
    const mode = useWatch({control: control, name: 'mode'})
    const customColumns = useWatch({control: control, name: 'customColumns'})

    // Sync isExporting state with exportAsync.isLoading
    useEffect(() => {
        setIsExporting(exportAsync.isLoading)
    }, [exportAsync.isLoading, setIsExporting])

    // On a successful async enqueue, "crumple" the dialog and fly it into the header exports button,
    // then close it — signalling where the queued export now lives. With reduced motion we keep the
    // static "queued" confirmation and let the user close it manually.
    useEffect(() => {
        if (!wasAsync || !exportAsync.isSuccess || hasFlownRef.current || prefersReducedMotion()) return
        hasFlownRef.current = true
        const dialogElement = dialog?.getState().contentElement ?? null
        void flyToExportsToggle(dialogElement).then(played => {
            // Only auto-close when the dialog actually flew out; otherwise keep the static
            // "queued" confirmation so the user is never left with an abruptly vanished dialog.
            if (played) {
                dialog?.hide()
                // Right after the dialog lands on the toggle, announce the pending job there. The
                // popover then transitions to ready/failed when the socket reports the outcome.
                // Guard against a fast export whose socket result already arrived during the ~700ms
                // animation: don't overwrite a fresher status the socket may have already announced.
                const pendingJob = enqueuedJobRef.current
                const announced = useExportAnnouncementStore.getState().job
                if (pendingJob && announced?.id !== pendingJob.id) {
                    useExportAnnouncementStore.getState().announce(pendingJob)
                }
            }
        })
    }, [wasAsync, exportAsync.isSuccess, dialog])

    return (
        <FormProvider {...form}>
            <DialogBody>
                {exportAsync.isLoading ? (
                    <Flexbox direction="column" gap={1} align="center" fullWidth>
                        <Spinner size={32} />
                        <Flexbox direction="column" gap={1} align="center">
                            <StyledSectionTitle>{t('commons:export_pending_title')}</StyledSectionTitle>
                            <StyledDescription>{t('commons:export_pending_paragraph')}</StyledDescription>
                        </Flexbox>
                    </Flexbox>
                ) : exportAsync.isError ? (
                    <Flexbox direction="column" gap={1} align="center" fullWidth>
                        <AlertCircleIcon size={32} />
                        <StyledSectionTitle>{error}</StyledSectionTitle>
                    </Flexbox>
                ) : exportAsync.isSuccess ? (
                    wasAsync ? (
                        <Flexbox direction="column" gap={1} align="center" fullWidth>
                            <CheckIcon size={32} fill={theme.palette.success['500']} />
                            <StyledSectionTitle>{t('exports:toast_queued')}</StyledSectionTitle>
                            <StyledDescription>{t('exports:queued_description')}</StyledDescription>
                        </Flexbox>
                    ) : (
                        <Flexbox direction="column" gap={1} align="center" fullWidth>
                            <CheckIcon size={32} fill={theme.palette.success['500']} />
                            <StyledSectionTitle>{t('commons:export_success_title')}</StyledSectionTitle>
                            <StyledDescription>
                                <Trans
                                    i18nKey="commons:export_success_paragraph"
                                    components={{
                                        cta: (
                                            <button
                                                disabled={!fileData}
                                                onClick={
                                                    fileData
                                                        ? () =>
                                                              downloadFile(
                                                                  buildFile({extension, fileName, data: fileData})
                                                              )
                                                        : undefined
                                                }
                                            />
                                        )
                                    }}
                                />
                            </StyledDescription>
                        </Flexbox>
                    )
                ) : (
                    <>
                        {hasViews && (
                            <DialogDescription>
                                <Trans
                                    i18nKey="commons:export_paragraph"
                                    values={{count: selectedCount, view: viewQuery.data?.name ?? t('commons:view_all')}}
                                    components={{b: <b />}}
                                />
                            </DialogDescription>
                        )}
                        <Flexbox direction="column" gap={3}>
                            <StyledSectionTitle>{t('commons:extension')}</StyledSectionTitle>
                            <Flexbox direction="column" gap={2} fullWidth>
                                {allowedExtensions.map(extension => (
                                    <StyledExtensionRadioField key={extension}>
                                        <InputRadio value={extension} {...register('extension')} />
                                        <div>
                                            <p>{extensionToLabel[extension]}</p>
                                            <small>{t(extensionToDescription[extension])}</small>
                                        </div>
                                    </StyledExtensionRadioField>
                                ))}
                            </Flexbox>
                        </Flexbox>
                        {hasViews && (
                            <Flexbox direction="column" gap={3}>
                                <StyledSectionTitle>{t('commons:columns_title')}</StyledSectionTitle>
                                <Flexbox direction="column" gap={2} fullWidth>
                                    <RadioField
                                        label={t('commons:columns_visible')}
                                        value="view"
                                        {...register('mode')}
                                    />
                                    <RadioField label={t('commons:columns_all')} value="all" {...register('mode')} />
                                    {entity && <ExportModalCustomColumns entity={entity} />}
                                </Flexbox>
                            </Flexbox>
                        )}
                    </>
                )}
            </DialogBody>
            <DialogFooter style={{justifyContent: 'flex-end', gap: theme.spacing * 2}}>
                <DialogDismiss disabled={exportAsync.isLoading} render={<Button variant="secondary" />}>
                    {exportAsync.isSuccess ? t('commons:close') : t('commons:cancel')}
                </DialogDismiss>
                {exportAsync.isIdle && (
                    <Button
                        variant="primaryNeutral"
                        onClick={onSubmit}
                        disabled={mode == 'custom' && (!customColumns || customColumns.length == 0)}
                    >
                        {t('commons:export')}
                    </Button>
                )}
            </DialogFooter>
        </FormProvider>
    )
}
